import {
  ConstrainMode,
  IconButton,
  IContextualMenuProps,
  PrimaryButton,
  SelectionMode,
  ShimmeredDetailsList,
  Stack
} from "@fluentui/react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { HeaderItemCount } from "../components/headerItemCount";
import { ListDataCommandBar } from "../components/listDataCommandBar";
import { ListDataFilters } from "../components/listDataFilters";
import { ListDataFiltersMassAction } from "../components/listDataFiltersMassAction";

import { useListFilters } from "./hooks/useListFilters";
import { usePersistentSelection } from "./hooks/usePersistentSelection";
import { ListDataProps } from "./interfaces";
import { listStyles } from "./styles";

import undrawNoData from "@/assets/images/undraw_no_data_qbuo.svg";
import { useURLFilter } from "@/hooks/useUrlFilter";

const _buildColumns = (columns, menuProps, selectItem) => {
  if (columns) {
    columns.forEach(column => {
      if (column.key === "actions") {
        column.onRender = item => (
          <IconButton
            id={`actions-${item.id}`}
            onMenuClick={() => {
              selectItem(item);
            }}
            menuProps={menuProps}
            iconProps={{ iconName: "MoreVertical" }}
            onRenderMenuIcon={() => null}
          />
        );
      }
    });
  }

  return columns;
};

/**
 * @deprecated Esta função será descontinuada.
 */
export function ListData(props: ListDataProps): JSX.Element {
  const {
    items,
    isLoadingList,
    headerAction,
    headerText,
    _paginate,
    filters,
    hasFilters,
    headerFilters,
    headerFilterQueryName,
    isDefaultFilterVisible,
    isCheckSelection,
    isItemsPerPageFilterDisabled,
    isCloseFilterButtonDisabled,
    selectionPreservedOnEmptyClick = false,
    layoutMode,
    canBeMultipleSelection,
    massActionsOptions,
    moduleHasMassUpdate = false
  } = props;
  const {
    massActionsType,
    setOpenMassActionsFilter,
    openMassActionsFilter,
    sendMassActionsFunction,
    buttonMassActionsIconName
  } = massActionsOptions ?? {
    massActionsType: null,
    setOpenMassActionsFilter: null,
    openMassActionsFilter: false,
    sendMassActionsFunction: null,
    buttonMassActionsIconName: null
  };

  const { setUrlParams } = useURLFilter();
  const location = useLocation();

  const {
    selectedKeys,
    setSelectedKeys,
    selectedTerms,
    setSelectedTerms,
    visibleTerms,
    setVisibleTerms,
    bulkOptionsFilters,
    setBulkOptionsFilters,
    debouncedTerms
  } = useListFilters(filters);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [allCurrentItems, setAllCurrentItems] = useState<any>([]);

  const [selectionMode] = useState<SelectionMode>(
    props.selectionMode || SelectionMode.none
  );

  const [isFilterVisible, setIsFilterVisible] = useState<boolean>(
    isDefaultFilterVisible ?? false
  );

  const renderCountRef = useRef(1);

  const { selection, itemsSelected, setItemsSelected } = usePersistentSelection(
    {
      items,
      allCurrentItems,
      isLoadingList,
      selectionMode,
      canBeMultipleSelection,
      isCheckSelection,
      setSelectedItem: props.setSelectedItem
    }
  );

  useEffect(() => {
    if (_paginate) {
      // Aguarda as duas primeiras renderizações antes de executar a paginação.
      // Essas execuções iniciais ocorrem enquanto os estados de filtro (debouncedTerms e bulkOptionsFilters) ainda estão se estabilizando
      // Veja mais detalhes em: libertas-web/issues/1145
      const MIN_RENDERS_BEFORE_PAGINATION = 3;

      if (renderCountRef.current < MIN_RENDERS_BEFORE_PAGINATION) {
        renderCountRef.current++;
      } else {
        const filters = { ...debouncedTerms };

        for (const key in filters) {
          if (typeof filters[key] === "string") {
            filters[key] = filters[key].trim();
          }
        }

        _paginate({
          limit: itemsPerPage ?? 10,
          ...selectedKeys,
          ...filters,
          ...bulkOptionsFilters
        });
      }
    }
  }, [debouncedTerms, bulkOptionsFilters]);

  useEffect(() => {
    setAllCurrentItems(items);
  }, [items]);

  const selectItem = useCallback(
    item => {
      if (props.setSelectedItem) props.setSelectedItem(item.id);
    },
    [props.setSelectedItem]
  );

  const toggleOpenMassActionsFilterVisibility = () => {
    setIsFilterVisible && setIsFilterVisible(false);
    setOpenMassActionsFilter(isOpen => !isOpen);
    setBulkOptionsFilters({ isLineStatus: false });
  };

  const toggleFilterVisibility = () => {
    setOpenMassActionsFilter && setOpenMassActionsFilter(false);
    setIsFilterVisible(prevValue => !prevValue);
  };

  const menuProps: IContextualMenuProps = {
    shouldFocusOnMount: true,
    items: props.menuItems
  };

  const columns = _buildColumns(props.columns, menuProps, selectItem);

  const handleHeaderItemLinkClick = currentKeys => {
    setSelectedKeys(currentKeys);
    _paginate({
      limit: itemsPerPage ?? 10,
      ...debouncedTerms,
      ...currentKeys
    });
  };

  return (
    <Fragment>
      <Stack
        horizontal
        horizontalAlign="space-between"
        styles={{
          root: {
            paddingTop: 12,
            paddingLeft: 12,
            paddingRight: 12
          }
        }}
      >
        <Stack.Item align="center">
          {!Array.isArray(allCurrentItems) && (
            <>Total: {allCurrentItems?.meta?.totalItems ?? 0}</>
          )}
          {itemsSelected && itemsSelected[0] && (
            <> {`|| Selecionados: ${itemsSelected.length ?? 0}`}</>
          )}
        </Stack.Item>

        <Stack.Item
          align="center"
          style={{
            justifyContent: "center",
            display: "flex"
          }}
        >
          {massActionsType && moduleHasMassUpdate && (
            <PrimaryButton
              iconProps={{ iconName: buttonMassActionsIconName }}
              text="Atualização em Massa"
              onClick={toggleOpenMassActionsFilterVisibility}
              style={{ marginRight: "4px" }}
            />
          )}
          {headerAction && (
            <PrimaryButton
              id={`add-${headerText.replace(/ /g, "-")}`}
              iconProps={{ iconName: "Add" }}
              text={headerText}
              onClick={headerAction}
            />
          )}
        </Stack.Item>
      </Stack>

      {headerFilters && (
        <Stack horizontalAlign="center">
          <HeaderItemCount
            selectedKeys={selectedKeys}
            handleLinkClick={handleHeaderItemLinkClick}
            filterQuery={headerFilterQueryName}
            filtersConfig={headerFilters}
          />
        </Stack>
      )}

      {!Array.isArray(allCurrentItems) && (
        <ListDataCommandBar
          cfg={allCurrentItems}
          _paginate={filters => {
            _paginate({
              limit: itemsPerPage ?? 10,
              ...debouncedTerms,
              ...selectedKeys,
              ...filters
            });
          }}
          hasFilters={hasFilters}
          resetSelectedItems={() => {
            setUrlParams({ pathname: location.pathname, params: {} });
            setSelectedKeys({});
            setSelectedTerms({});
            setVisibleTerms({});
          }}
          resetItemsPerPage={() => setItemsPerPage(undefined)}
          toggleFilterVisibility={toggleFilterVisibility}
        />
      )}

      {isFilterVisible && (
        <ListDataFilters
          setSelectedKeys={setSelectedKeys}
          setVisibleTerms={setVisibleTerms}
          setSelectedTerms={setSelectedTerms}
          toggleFilterVisibility={toggleFilterVisibility}
          filters={filters}
          _paginate={_paginate}
          itemsPerPage={itemsPerPage}
          visibleTerms={visibleTerms}
          selectedKeys={selectedKeys}
          selectedTerms={selectedTerms}
          debouncedTerms={debouncedTerms}
          isCloseFilterButtonDisabled={isCloseFilterButtonDisabled}
          isItemsPerPageFilterDisabled={isItemsPerPageFilterDisabled}
        />
      )}

      {massActionsType && (
        <ListDataFiltersMassAction
          setSelectedKeys={setSelectedKeys}
          setVisibleTerms={setVisibleTerms}
          setSelectedTerms={setSelectedTerms}
          toggleFilterVisibility={toggleOpenMassActionsFilterVisibility}
          items={items}
          filters={filters}
          _paginate={filters =>
            _paginate({ ...filters, ...bulkOptionsFilters })
          }
          itemsPerPage={itemsPerPage}
          visibleTerms={visibleTerms}
          selectedKeys={selectedKeys}
          selectedTerms={selectedTerms}
          debouncedTerms={debouncedTerms}
          isCloseFilterButtonDisabled={isCloseFilterButtonDisabled}
          isItemsPerPageFilterDisabled={isItemsPerPageFilterDisabled}
          massActionsType={massActionsType ?? null}
          sendMassActionsFunction={sendMassActionsFunction}
          selectedItemsIds={itemsSelected[0] && itemsSelected?.map(i => i.id)}
          itemsSelection={itemsSelected}
          setItemsSelected={setItemsSelected}
          openMassActionsFilter={openMassActionsFilter}
        />
      )}

      <ShimmeredDetailsList
        setKey="items"
        items={
          Array.isArray(allCurrentItems)
            ? allCurrentItems
            : allCurrentItems?.items
        }
        columns={columns}
        selection={selection}
        ariaLabelForSelectionColumn="Toggle selection"
        ariaLabelForSelectAllCheckbox="Toggle selection for all items"
        checkButtonAriaLabel="Row checkbox"
        constrainMode={ConstrainMode.horizontalConstrained}
        layoutMode={layoutMode}
        selectionMode={openMassActionsFilter ? 2 : selectionMode}
        selectionPreservedOnEmptyClick={selectionPreservedOnEmptyClick}
        enableShimmer={isLoadingList}
        detailsListStyles={listStyles}
      />
      {!isLoadingList &&
        !Array.isArray(allCurrentItems) &&
        allCurrentItems?.meta?.totalItems === 0 && (
          <Stack
            styles={{
              root: {
                alignItems: "center"
              }
            }}
          >
            <img
              src={undrawNoData}
              title="Nenhum dado encontrado no banco"
              alt="Nenhum dado encontrado no banco"
              style={{ width: "55vh", height: "55vh" }}
            />
          </Stack>
        )}
    </Fragment>
  );
}

export default ListData;
